import { defaultFilter } from 'cmdk';
import { formatPluginName } from '@reflex/ui/lib/format';
import type { InstallationView, OrgPluginListResponse } from '@/lib/types';
import type { PluginManifest } from '@/lib/plugin-registry';

export type PluginStatusFilter = 'active' | 'disabled' | null;
export type PluginTypeFilter =
  | 'server'
  | 'web'
  | 'auth'
  | 'webhook-provider'
  | 'webhook-receiver'
  | null;
export type PluginTagFilter = string | null;

export interface PluginFilters {
  status: PluginStatusFilter;
  type: PluginTypeFilter;
  tag: PluginTagFilter;
  query: string;
}

/**
 * Fixed score for plugins that only match on their description. Descriptions
 * are long and noisy, so they are matched by plain substring (never a scattered
 * subsequence) and always rank beneath any name/tag hit. cmdk scores are in
 * (0, 1], so any positive name/tag match outranks this.
 */
const DESCRIPTION_MATCH_SCORE = 0.001;

/**
 * Scores a plugin against the query, returning 0 for no match. Name and tags go
 * through cmdk's `defaultFilter` (the same scorer the command palette uses),
 * which ranks exact > prefix > word-boundary > substring > fuzzy subsequence.
 * Descriptions only contribute a plain substring match so unrelated plugins do
 * not leak in via letter-order coincidences (e.g. "linear" in "impersonation").
 */
function scoreQuery(plugin: PluginManifest, query: string): number {
  const keywords = [plugin.name, ...(plugin.tags ?? [])];
  const nameScore = defaultFilter(formatPluginName(plugin.name), query, keywords);
  if (nameScore > 0) return nameScore;
  if (plugin.description && plugin.description.toLowerCase().includes(query.toLowerCase())) {
    return DESCRIPTION_MATCH_SCORE;
  }
  return 0;
}

function matchesStructuralFilters(plugin: PluginManifest, filters: PluginFilters): boolean {
  if (filters.status !== null) {
    const isActive = plugin.enabled !== false;
    if (filters.status === 'active' && !isActive) return false;
    if (filters.status === 'disabled' && isActive) return false;
  }

  if (filters.type !== null) {
    if (filters.type === 'server' && !plugin.server) return false;
    if (filters.type === 'web' && !plugin.web) return false;
    if (filters.type === 'auth' && !plugin.server?.authProvider) return false;
    if (filters.type === 'webhook-provider' && !plugin.server?.webhookProvider) return false;
    if (filters.type === 'webhook-receiver' && !plugin.server?.webhookReceivers?.length)
      return false;
  }

  if (filters.tag !== null) {
    if (!plugin.tags?.includes(filters.tag)) return false;
  }

  return true;
}

export function filterPlugins(plugins: PluginManifest[], filters: PluginFilters): PluginManifest[] {
  const structural = plugins.filter((p) => matchesStructuralFilters(p, filters));

  const query = filters.query.trim();
  if (!query) return structural;

  // Array.sort is stable, so plugins with equal scores keep their original
  // (server-provided) order.
  return structural
    .map((plugin) => ({ plugin, score: scoreQuery(plugin, query) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((entry) => entry.plugin);
}

/**
 * Scores an org installation-view row against a query using the same strategy
 * as `scoreQuery`: cmdk `defaultFilter` for name and tags, plain substring for
 * descriptions.
 */
function scoreInstallationView(row: InstallationView, query: string): number {
  const keywords = [row.pluginName, ...(row.tags ?? [])];
  const nameScore = defaultFilter(formatPluginName(row.pluginName), query, keywords);
  if (nameScore > 0) return nameScore;
  if (row.description && row.description.toLowerCase().includes(query.toLowerCase())) {
    return DESCRIPTION_MATCH_SCORE;
  }
  return 0;
}

function rankInstallationViews(rows: InstallationView[], query: string): InstallationView[] {
  return rows
    .map((row) => ({ row, score: scoreInstallationView(row, query) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((entry) => entry.row);
}

/**
 * Filters and ranks all three buckets of an org plugin list by the given
 * search query. Uses cmdk scoring on name/tags and plain substring on
 * descriptions so false positives from scattered subsequences are prevented.
 * Returns the original data unchanged when the query is blank.
 */
export function filterOrgPlugins(
  data: OrgPluginListResponse,
  query: string,
): OrgPluginListResponse {
  const q = query.trim();
  if (!q) return data;
  return {
    installed: rankInstallationViews(data.installed, q),
    available: rankInstallationViews(data.available, q),
    system: rankInstallationViews(data.system, q),
  };
}

/** Collects all unique tags across all plugins, sorted alphabetically. */
export function collectTags(plugins: PluginManifest[]): string[] {
  const tags = new Set<string>();
  for (const p of plugins) {
    for (const t of p.tags ?? []) {
      tags.add(t);
    }
  }
  return [...tags].sort();
}
